package com.arena_web.ui.components.feed

import androidx.compose.runtime.Composable
import com.arena_web.data.dto.FeedStoryAuthorGroupDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

/**
 * Figma 8702:137599 — horizontal carousel of story tiles for the Feed screen.
 *
 * Tile dimensions per design: 189×190, 12px radius, 16px gap. Each tile shows
 * the author's newest story image with a small player-card badge overlaid in
 * the bottom-left corner. When the viewer has not opened all of that author's
 * stories yet the image is rendered behind a frosted-glass overlay so the
 * preview teases without giving away the content — once the viewer has seen
 * everything the overlay drops to make it clear the row is "all caught up".
 */
@Composable
fun FeedStoriesRow(
    groups: List<FeedStoryAuthorGroupDto>,
    onAuthorClick: (FeedStoryAuthorGroupDto) -> Unit,
    onLoadMore: () -> Unit = {},
    modifier: Modifier = Modifier,
) {
    if (groups.isEmpty()) return

    // Horizontal scroll container — we use a raw Div instead of Row so we
    // can flip overflow-x to auto and let the carousel scroll independently
    // of the page. Scroll listener triggers `onLoadMore` when the user
    // approaches the right edge so the next page of story authors streams in.
    Div(attrs = {
        ref { el ->
            val handler: (org.w3c.dom.events.Event) -> Unit = {
                val maxScroll = el.scrollWidth - el.clientWidth
                if (maxScroll - el.scrollLeft < 300) onLoadMore()
            }
            el.addEventListener("scroll", handler)
            onDispose { el.removeEventListener("scroll", handler) }
        }
        style {
            property("display", "flex")
            property("gap", s(16))
            property("width", "100%")
            property("overflow-x", "auto")
            property("overflow-y", "hidden")
            property("scrollbar-width", "thin")
            property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
            property("padding", "${s(2)} ${s(2)}")
        }
    }) {
        groups.forEach { group ->
            StoryTile(group = group, onClick = { onAuthorClick(group) })
        }
    }
}

@Composable
private fun StoryTile(
    group: FeedStoryAuthorGroupDto,
    onClick: () -> Unit,
) {
    // Per Figma 8702:137601 — tile 189×190, image fills the tile, frosted
    // blur on top. Wrapper is bigger by `shift` on both axes so a notch
    // appears in the bottom-left where the player card sits.
    val tileWidth = 189
    val tileHeight = 190
    // Story tile is shifted RIGHT and UP by `shift` px, freeing a square in
    // the bottom-left corner for the player-card badge.
    val shift = 10
    val previewUrl = group.stories.lastOrNull()?.imageUrl

    // Outer wrapper — no overflow:clip so the card can sit in the
    // bottom-left notch carved out by the shifted image tile.
    Box(
        modifier = Modifier
            .styleModifier {
                property("flex", "0 0 auto")
                property("position", "relative")
                property("width", "calc(${s(tileWidth)} + ${s(shift)})")
                property("height", "calc(${s(tileHeight)} + ${s(shift)})")
                property("cursor", "pointer")
            }
            .onClick { onClick() },
    ) {
        // Rounded image tile — anchored to TOP-RIGHT of the wrapper, leaving
        // a `shift`×`shift` notch in the bottom-left for the player card.
        Box(
            modifier = Modifier
                .styleModifier {
                    property("position", "absolute")
                    property("top", "0")
                    property("right", "0")
                    property("width", s(tileWidth))
                    property("height", s(tileHeight))
                    property("border-radius", s(12))
                    property("overflow", "hidden")
                    // Blue ring only for single-story unseen groups. When the
                    // author has multiple stories we drop both the ring and
                    // the count pill — visual noise the user opted out of.
                    if (!group.allSeen && group.stories.size <= 1) {
                        property("border", "2px solid #0061ff")
                    }
                }
        ) {
            if (previewUrl != null) {
                Image(
                    src = previewUrl,
                    modifier = Modifier
                        .styleModifier {
                            property("position", "absolute")
                            property("inset", "0")
                            property("width", "100%")
                            property("height", "100%")
                            property("object-fit", "cover")
                            property("object-position", "center")
                            property("display", "block")
                        }
                )
            }

            // Frosted blur overlay — only for UNSEEN stories. Pure blur, no
            // grey tint, so the image keeps its original colors.
            if (!group.allSeen) {
                Box(
                    modifier = Modifier.fillMaxSize().styleModifier {
                        property("position", "absolute")
                        property("inset", "0")
                        property("backdrop-filter", "blur(3.5px)")
                        property("-webkit-backdrop-filter", "blur(3.5px)")
                    }
                )
            }

        }

        // Player-card badge — anchored to the BOTTOM-LEFT corner of the
        // wrapper, sitting in the notch the shifted image left behind.
        Box(
            modifier = Modifier
                .styleModifier {
                    property("position", "absolute")
                    property("left", "0")
                    property("bottom", "0")
                    property("pointer-events", "none")
                }
        ) {
            val cardFrameUrl = group.cardImageUrl
            if (cardFrameUrl != null) {
                PlayerCard(
                    playerName = "",
                    rating = 0,
                    position = "",
                    sport = "football",
                    size = PlayerCardSize.XtraSmall,
                    cardAssetsBasePath = group.cardAssetsBasePath,
                    cardTextColor = group.cardTextColor,
                    cardFrameUrl = cardFrameUrl,
                    avatarUrl = group.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                    hideRating = true,
                    hideShadow = true,
                )
            } else {
                // Fallback — round avatar when the author has no card.
                Box(
                    modifier = Modifier
                        .sSize(40)
                        .sBorderRadius(20)
                        .sMargin(8)
                        .styleModifier {
                            property("background-color", "#1A2238")
                            property("overflow", "hidden")
                            property("border", "1.5px solid rgba(255,255,255,0.6)")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    val avatar = group.authorAvatarUrl
                    if (avatar != null) {
                        Div(attrs = {
                            ref { el ->
                                el.innerHTML =
                                    "<img src=\"$avatar\" referrerpolicy=\"no-referrer\" " +
                                        "style=\"width:100%;height:100%;object-fit:cover;\" />"
                                onDispose { }
                            }
                            style { property("width", "100%"); property("height", "100%") }
                        })
                    } else {
                        SpanText(
                            group.authorName.take(1).uppercase(),
                            modifier = Modifier
                                .sFontSize(14)
                                .fontWeight(AppTypography.Medium)
                                .color(com.arena_web.ui.theme.ThemeVars.Text1)
                        )
                    }
                }
            }
        }
    }
}
